import React, { useEffect, useState, useCallback } from "react";
import { Loader2, CheckCircle2, XCircle, Bell, CalendarClock, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function ProposalCard({ p, onDone }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [date, setDate] = useState(p.proposed_date || "");
  const [insts, setInsts] = useState((p.installments || []).map((i) => ({ amount: String(i.amount), date: i.date })));
  const total = insts.reduce((s, i) => s + (parseInt(i.amount, 10) || 0), 0);
  const decided = p.status !== "pending";

  const approve = async () => {
    setBusy(true);
    try {
      const body = { admin_note: note };
      if (p.type === "monthly_date") body.proposed_date = date;
      else body.installments = insts.map((i) => ({ amount: parseInt(i.amount, 10) || 0, date: i.date }));
      await api.post(`/fees/schedule-proposals/${p.id}/approve`, body);
      toast.success("Proposal approved & schedule applied");
      onDone && onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not approve"); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try { await api.post(`/fees/schedule-proposals/${p.id}/reject`, { admin_note: note }); toast.success("Proposal rejected"); onDone && onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not reject"); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`proposal-${p.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div><p className="font-bold text-slate-900">{p.student_name} · {p.roll_number}</p>
          <p className="text-xs text-slate-500">{p.type === "monthly_date" ? "Monthly due-date request" : p.type === "collect_plan" ? "Lump-sum plan change request" : "Lump-sum instalments"} · {formatDate(p.created_at)}</p></div>
        <Badge className={p.status === "pending" ? "bg-amber-500" : p.status === "approved" ? "bg-green-600" : "bg-red-600"}>{p.status}</Badge>
      </div>
      {p.type === "monthly_date" ? (
        <div className="text-sm"><span className="text-slate-500">Month {p.monthly_period}, proposed date:</span>
          {decided ? <b className="ml-1">{(p.final && p.final.proposed_date) || p.proposed_date}</b> : <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid={`proposal-date-${p.id}`} />}
        </div>
      ) : (
        <div className="space-y-2">
          {p.current_rows && p.current_rows.length > 0 && <p className="text-xs text-slate-500" data-testid={`proposal-current-${p.id}`}>Current plan: {p.current_rows.map((r) => `${inr(r.amount)} by ${formatDate(r.date)}${r.status === "paid" ? " (paid)" : ""}`).join(" · ")}</p>}
          {(decided ? ((p.final && p.final.installments) || p.installments || []) : insts).map((it, i) => (
            <div key={i} className="flex gap-2 items-end text-sm" data-testid={`proposal-inst-${p.id}-${i}`}>
              {decided ? <span>{p.type === "collect_plan" ? "Row" : "Instalment"} {i + 1}: <b>{inr(it.amount)}</b> {p.type === "collect_plan" ? "by" : "due"} {formatDate(it.due_date || it.date)}</span> : (<>
                <div className="flex-1"><Label className="text-[11px]">Amount ₹</Label><Input type="number" value={it.amount} onChange={(e) => setInsts((a) => a.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} /></div>
                <div className="flex-1"><Label className="text-[11px]">Date</Label><Input type="date" value={it.date} onChange={(e) => setInsts((a) => a.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} /></div>
              </>)}
            </div>
          ))}
          {!decided && <p className="text-xs text-slate-500" data-testid={`proposal-total-${p.id}`}>Total: {inr(total)} {p.type === "collect_plan" ? "(unpaid rows can't exceed the outstanding)" : "(must equal the student's outstanding)"}</p>}
        </div>
      )}
      {p.note && <p className="text-xs text-slate-600">Parent note: {p.note}</p>}
      {p.admin_note && <p className="text-xs text-slate-600">Admin note: {p.admin_note}</p>}
      {!decided && (
        <>
          <Input placeholder="Note to parent (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`proposal-note-${p.id}`} />
          <div className="flex gap-2">
            <Button onClick={approve} disabled={busy} className="flex-1 bg-green-600 hover:bg-green-700" data-testid={`proposal-approve-${p.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</>}</Button>
            <Button onClick={reject} disabled={busy} variant="outline" className="flex-1 text-red-600 border-red-300" data-testid={`proposal-reject-${p.id}`}><XCircle className="h-4 w-4 mr-1" /> Reject</Button>
          </div>
        </>
      )}
    </div>
  );
}

export default function FeeProposals() {
  const [proposals, setProposals] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const [pr, al] = await Promise.all([api.get("/fees/schedule-proposals"), api.get("/fees/admin/alerts")]);
      setProposals(pr.data); setAlerts(al.data);
    } catch { toast.error("Could not load proposals"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolveAlert = async (id) => { try { await api.post(`/fees/admin/alerts/${id}/resolve`); load(); } catch { /* noop */ } };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;
  const pending = proposals.filter((p) => p.status === "pending");
  const decided = proposals.filter((p) => p.status !== "pending");

  return (
    <div className="space-y-6" data-testid="fee-proposals">
      <section>
        <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-2"><Bell className="h-5 w-5" /> Fee alerts {alerts.length > 0 && <Badge className="bg-red-600">{alerts.length}</Badge>}</h3>
        {alerts.length === 0 ? <p className="text-sm text-slate-500">No active alerts.</p> : (
          <div className="space-y-2">
            {alerts.map((a) => (
              <div key={a.id} className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-center justify-between gap-2 text-sm" data-testid={`fee-alert-${a.id}`}>
                <span className="text-amber-900">{a.message}</span>
                <Button size="sm" variant="outline" onClick={() => resolveAlert(a.id)} data-testid={`fee-alert-resolve-${a.id}`}>Dismiss</Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-2"><CalendarClock className="h-5 w-5" /> Pending schedule proposals {pending.length > 0 && <Badge className="bg-amber-500">{pending.length}</Badge>}</h3>
        {pending.length === 0 ? <p className="text-sm text-slate-500">No proposals awaiting review.</p> : (
          <div className="space-y-3">{pending.map((p) => <ProposalCard key={p.id} p={p} onDone={load} />)}</div>
        )}
      </section>

      {decided.length > 0 && (
        <section>
          <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-2"><IndianRupee className="h-5 w-5" /> Decided proposals</h3>
          <div className="space-y-3">{decided.slice(0, 20).map((p) => <ProposalCard key={p.id} p={p} onDone={load} />)}</div>
        </section>
      )}
    </div>
  );
}
